import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";

interface BookingRow {
  id: string;
  service_name: string;
  booking_date: string;
  booking_time: string;
  status: string;
  created_at: string;
}

const STATUS_LABEL: Record<string, { label: string; className: string }> = {
  pending: {
    label: "Chờ xác nhận",
    className: "bg-accent-100 text-accent-700",
  },
  confirmed: {
    label: "Đã xác nhận",
    className: "bg-primary-100 text-primary-700",
  },
  completed: {
    label: "Đã hoàn thành",
    className: "bg-secondary-100 text-secondary-900",
  },
  cancelled: {
    label: "Đã hủy",
    className: "bg-background-200 text-foreground-500",
  },
};

function formatDate(dateKey: string): string {
  const [y, m, d] = dateKey.split("-").map(Number);
  return `${d}/${m}/${y}`;
}

export default function Profile() {
  const { user, profile, loading: authLoading, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadBookings = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError("");
    try {
      const { data, error: fetchError } = await supabase
        .from("bookings")
        .select("id, service_name, booking_date, booking_time, status, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (fetchError) {
        setError("Không thể tải lịch sử đặt lịch. Vui lòng thử lại.");
        return;
      }
      setBookings((data as BookingRow[]) ?? []);
    } catch {
      setError("Không thể tải lịch sử đặt lịch. Vui lòng thử lại.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      void loadBookings();
    }
  }, [user, loadBookings]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  if (authLoading) {
    return (
      <MainLayout>
        <div className="w-full min-h-[60vh] flex items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
        </div>
      </MainLayout>
    );
  }

  if (!user) {
    return (
      <MainLayout>
        <div className="w-full min-h-[60vh] flex flex-col items-center justify-center px-4 py-20">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-5">
            <i className="ri-lock-line text-2xl" />
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground-950 mb-3 text-center">
            Vui lòng đăng nhập
          </h1>
          <p className="text-sm md:text-base text-foreground-600 text-center max-w-md mb-6 font-body">
            Đăng nhập để xem lịch sử đặt lịch và thông tin cá nhân của bạn.
          </p>
          <Link
            to="/login"
            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors"
          >
            <i className="ri-login-box-line mr-2" />
            Đăng nhập ngay
          </Link>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="w-full min-h-screen bg-background-50 pt-24 md:pt-28 pb-20 px-4 md:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Profile header */}
          <div className="bg-white rounded-lg border border-background-200/70 p-6 md:p-8 mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                <i className="ri-user-line text-2xl" />
              </div>
              <div className="flex-1">
                <h1 className="font-display text-2xl font-bold text-foreground-950">
                  {profile?.name || "Khách hàng"}
                </h1>
                <p className="text-sm text-foreground-600 font-body mt-0.5">
                  {profile?.email || user.email}
                </p>
                {profile?.phone && (
                  <p className="text-sm text-foreground-500 font-body mt-0.5">
                    <i className="ri-phone-line mr-1" />
                    {profile.phone}
                  </p>
                )}
              </div>
              <button
                onClick={handleSignOut}
                className="px-5 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 border border-background-300/60 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors cursor-pointer"
              >
                <i className="ri-logout-box-line mr-2" />
                Đăng xuất
              </button>
            </div>
          </div>

          {/* Admin shortcut */}
          {isAdmin && (
            <div className="bg-white rounded-lg border border-primary-200/70 p-5 md:p-6 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                <i className="ri-dashboard-line text-xl" />
              </div>
              <div className="flex-1">
                <h2 className="font-display text-lg font-bold text-foreground-950">
                  Khu vực quản trị
                </h2>
                <p className="text-sm text-foreground-600 font-body mt-0.5">
                  Quản lý lịch hẹn và dịch vụ của website.
                </p>
              </div>
              <Link
                to="/admin/bookings"
                className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors flex items-center gap-2"
              >
                <i className="ri-arrow-right-line" />
                Vào trang quản trị
              </Link>
            </div>
          )}

          {/* Booking history */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-semibold text-foreground-950">
              Lịch sử đặt lịch
            </h2>
            <Link
              to="/booking"
              className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors"
            >
              <i className="ri-add-line mr-1" />
              Đặt lịch mới
            </Link>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-500" />
            </div>
          ) : error ? (
            <div className="bg-white rounded-lg border border-background-200/70 p-8 text-center">
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                <i className="ri-error-warning-line text-xl" />
              </div>
              <p className="text-sm text-foreground-600 font-body mb-4">{error}</p>
              <button
                onClick={() => void loadBookings()}
                className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-label text-sm font-medium whitespace-nowrap rounded-md transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line mr-2" />
                Thử lại
              </button>
            </div>
          ) : bookings.length === 0 ? (
            <div className="bg-white rounded-lg border border-background-200/70 p-8 text-center">
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-3">
                <i className="ri-calendar-line text-xl" />
              </div>
              <p className="text-sm text-foreground-600 font-body">
                Bạn chưa có lịch hẹn nào.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {bookings.map((booking) => {
                const status = STATUS_LABEL[booking.status] ?? STATUS_LABEL.pending;
                return (
                  <div
                    key={booking.id}
                    className="bg-white rounded-lg border border-background-200/70 p-4 md:p-5 flex items-center gap-4"
                  >
                    <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 flex-shrink-0">
                      <i className="ri-calendar-check-line text-xl" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground-900 font-body text-sm md:text-base">
                        {booking.service_name}
                      </p>
                      <p className="text-xs text-foreground-500 font-label mt-0.5">
                        {formatDate(booking.booking_date)} - {booking.booking_time}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-label font-medium whitespace-nowrap ${status.className}`}
                    >
                      {status.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}